/**
 * MediaPipe Pose Estimation — Lazy-loaded WASM wrapper
 *
 * Initializes PoseLandmarker with lite model at 30fps.
 * Runs entirely client-side — no video frames sent to server.
 * Returns 33 3D landmarks per frame (BlazePose topology).
 */

import {
  PoseLandmarker,
  FilesetResolver,
  type PoseLandmarkerResult,
  type NormalizedLandmark,
} from '@mediapipe/tasks-vision';

export type { NormalizedLandmark, PoseLandmarkerResult };

/** Named landmark indices from BlazePose (33 points) */
export const LANDMARK = {
  NOSE: 0,
  LEFT_EYE_INNER: 1,
  LEFT_EYE: 2,
  LEFT_EYE_OUTER: 3,
  RIGHT_EYE_INNER: 4,
  RIGHT_EYE: 5,
  RIGHT_EYE_OUTER: 6,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  MOUTH_LEFT: 9,
  MOUTH_RIGHT: 10,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_PINKY: 17,
  RIGHT_PINKY: 18,
  LEFT_INDEX: 19,
  RIGHT_INDEX: 20,
  LEFT_THUMB: 21,
  RIGHT_THUMB: 22,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
  LEFT_HEEL: 29,
  RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31,
  RIGHT_FOOT_INDEX: 32,
} as const;

let landmarkerInstance: PoseLandmarker | null = null;
let initPromise: Promise<PoseLandmarker> | null = null;

/**
 * Lazily initialize the PoseLandmarker (downloads WASM + model on first call).
 * Subsequent calls return the cached instance.
 */
export async function getPoseLandmarker(): Promise<PoseLandmarker> {
  if (landmarkerInstance) return landmarkerInstance;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm'
      );

      landmarkerInstance = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });

      return landmarkerInstance;
    } catch (err) {
      // Clear promise so retry is possible
      initPromise = null;
      throw err;
    }
  })();

  return initPromise;
}

/**
 * Calculate the angle (in degrees) at point B, formed by points A-B-C.
 * Used for joint angle heuristics (e.g., elbow angle from shoulder-elbow-wrist).
 */
export function angleBetweenPoints(
  a: NormalizedLandmark,
  b: NormalizedLandmark,
  c: NormalizedLandmark,
): number {
  const radians = Math.atan2(c.y - b.y, c.x - b.x) - Math.atan2(a.y - b.y, a.x - b.x);
  let degrees = Math.abs(radians * (180 / Math.PI));
  if (degrees > 180) degrees = 360 - degrees;
  return degrees;
}

/**
 * Calculate average visibility/confidence across a set of landmarks.
 */
export function avgConfidence(landmarks: NormalizedLandmark[], indices: number[]): number {
  if (indices.length === 0) return 0;
  const sum = indices.reduce((acc, i) => {
    const lm = landmarks[i];
    return acc + (lm?.visibility ?? 0);
  }, 0);
  return sum / indices.length;
}

/**
 * Cleanup — call when component unmounts.
 */
export function disposePoseLandmarker(): void {
  if (landmarkerInstance) {
    landmarkerInstance.close();
    landmarkerInstance = null;
    initPromise = null;
  }
}
